import { getDay, subDays } from 'date-fns'
import { formatInTimeZone, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz'

import type {
  LockDeskCutoffControls,
  LockDeskCutoffDay,
  LockDeskCutoffProduct,
  LockDeskCutoffProductTimes,
  LockDeskCutoffSchedule,
  LockDeskCutoffTime,
  LockDeskCutoffTimezone,
  LockDeskCutoffTimezoneTimes,
  LockDeskOpenProductState,
  LockDeskOpenState,
} from '@mortgage-pos/types'

export const LOCK_DESK_CUTOFF_WINDOW_MS = 15 * 60 * 1000
export const LOCK_DESK_CUTOFF_EASTERN_IANA = 'America/New_York'

export const LOCK_DESK_CUTOFF_DAYS = [
  { key: 'sunday', label: 'Sunday' },
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
] as const satisfies ReadonlyArray<{ key: LockDeskCutoffDay; label: string }>

export const LOCK_DESK_CUTOFF_TIMEZONES = [
  { key: 'pacific', label: 'Pacific' },
  { key: 'mountain', label: 'Mountain' },
  { key: 'central', label: 'Central' },
  { key: 'eastern', label: 'Eastern' },
] as const satisfies ReadonlyArray<{
  key: LockDeskCutoffTimezone
  label: string
}>

export const LOCK_DESK_CUTOFF_PRODUCTS = [
  { key: 'conventional', label: 'Conventional & Govies' },
  { key: 'jumbo', label: 'Jumbo' },
  { key: 'arm', label: 'ARM' },
] as const satisfies ReadonlyArray<{
  key: LockDeskCutoffProduct
  label: string
}>

export const LOCK_DESK_CUTOFF_TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/

const LOOSE_TIME_PATTERN = /^(\d{1,2}):(\d{2})$/

export const LOCK_DESK_CUTOFF_TIMEZONE_LABELS: Record<
  LockDeskCutoffTimezone,
  string
> = {
  pacific: 'Pacific',
  mountain: 'Mountain',
  central: 'Central',
  eastern: 'Eastern',
}

export const LOCK_DESK_CUTOFF_PRODUCT_LABELS: Record<
  LockDeskCutoffProduct,
  string
> = {
  conventional: 'Conventional & Govies',
  jumbo: 'Jumbo',
  arm: 'ARM',
}

export interface DueLockDeskCutoff {
  timezone: LockDeskCutoffTimezone
  product: LockDeskCutoffProduct
  instant: Date
}

const EMPTY_PRODUCT_TIMES: LockDeskCutoffProductTimes = {
  conventional: null,
  jumbo: null,
  arm: null,
}

const EMPTY_TIMEZONE_TIMES: LockDeskCutoffTimezoneTimes = {
  pacific: { ...EMPTY_PRODUCT_TIMES },
  mountain: { ...EMPTY_PRODUCT_TIMES },
  central: { ...EMPTY_PRODUCT_TIMES },
  eastern: { ...EMPTY_PRODUCT_TIMES },
}

const EMPTY_OPEN_PRODUCT_STATE: LockDeskOpenProductState = {
  conventional: false,
  jumbo: false,
  arm: false,
}

export function isValidLockDeskCutoffTime(value: LockDeskCutoffTime): boolean {
  return value == null || LOCK_DESK_CUTOFF_TIME_PATTERN.test(value)
}

export function createEmptyLockDeskCutoffSchedule(): LockDeskCutoffSchedule {
  return {
    sunday: cloneTimezoneTimes(EMPTY_TIMEZONE_TIMES),
    monday: cloneTimezoneTimes(EMPTY_TIMEZONE_TIMES),
    tuesday: cloneTimezoneTimes(EMPTY_TIMEZONE_TIMES),
    wednesday: cloneTimezoneTimes(EMPTY_TIMEZONE_TIMES),
    thursday: cloneTimezoneTimes(EMPTY_TIMEZONE_TIMES),
    friday: cloneTimezoneTimes(EMPTY_TIMEZONE_TIMES),
    saturday: cloneTimezoneTimes(EMPTY_TIMEZONE_TIMES),
  }
}

export function normalizeLockDeskCutoffSchedule(
  raw: unknown
): LockDeskCutoffSchedule {
  const source =
    raw && typeof raw === 'object'
      ? (raw as Partial<LockDeskCutoffSchedule>)
      : {}

  return LOCK_DESK_CUTOFF_DAYS.reduce((schedule, day) => {
    const daySource = source[day.key]
    schedule[day.key] = LOCK_DESK_CUTOFF_TIMEZONES.reduce(
      (timezoneTimes, timezone) => {
        const timezoneSource = daySource?.[timezone.key]
        timezoneTimes[timezone.key] = LOCK_DESK_CUTOFF_PRODUCTS.reduce(
          (productTimes, product) => {
            productTimes[product.key] = normalizeCutoffTime(
              timezoneSource?.[product.key]
            )
            return productTimes
          },
          { ...EMPTY_PRODUCT_TIMES }
        )
        return timezoneTimes
      },
      cloneTimezoneTimes(EMPTY_TIMEZONE_TIMES)
    )
    return schedule
  }, createEmptyLockDeskCutoffSchedule())
}

export function canonicalizeCutoffTime(
  value: LockDeskCutoffTime
): LockDeskCutoffTime {
  if (value == null) {
    return null
  }

  const match = LOOSE_TIME_PATTERN.exec(value)
  if (!match) {
    return value
  }

  const padded = `${match[1].padStart(2, '0')}:${match[2]}`
  return LOCK_DESK_CUTOFF_TIME_PATTERN.test(padded) ? padded : value
}

export function cloneTimezoneTimes(
  timezoneTimes: LockDeskCutoffTimezoneTimes
): LockDeskCutoffTimezoneTimes {
  return {
    pacific: { ...timezoneTimes.pacific },
    mountain: { ...timezoneTimes.mountain },
    central: { ...timezoneTimes.central },
    eastern: { ...timezoneTimes.eastern },
  }
}

export function createEmptyLockDeskOpenState(): LockDeskOpenState {
  return {
    pacific: { ...EMPTY_OPEN_PRODUCT_STATE },
    mountain: { ...EMPTY_OPEN_PRODUCT_STATE },
    central: { ...EMPTY_OPEN_PRODUCT_STATE },
    eastern: { ...EMPTY_OPEN_PRODUCT_STATE },
  }
}

export function normalizeLockDeskOpenState(raw: unknown): LockDeskOpenState {
  const source =
    raw && typeof raw === 'object' ? (raw as Partial<LockDeskOpenState>) : {}

  return LOCK_DESK_CUTOFF_TIMEZONES.reduce((state, timezone) => {
    const timezoneSource = source[timezone.key]
    state[timezone.key] = LOCK_DESK_CUTOFF_PRODUCTS.reduce(
      (productState, product) => {
        productState[product.key] = timezoneSource?.[product.key] === true
        return productState
      },
      { ...EMPTY_OPEN_PRODUCT_STATE }
    )
    return state
  }, createEmptyLockDeskOpenState())
}

export const DEFAULT_LOCK_DESK_CUTOFF_CONTROLS: LockDeskCutoffControls = {
  job_enabled: false,
  slack_enabled: false,
  apply_cutoffs_enabled: false,
  use_new_cutoffs: false,
}

export function normalizeLockDeskCutoffControls(
  raw: unknown
): LockDeskCutoffControls {
  const source =
    raw && typeof raw === 'object'
      ? (raw as Partial<LockDeskCutoffControls>)
      : {}

  return {
    job_enabled: resolveControl(source.job_enabled, 'job_enabled'),
    slack_enabled: resolveControl(source.slack_enabled, 'slack_enabled'),
    apply_cutoffs_enabled: resolveControl(
      source.apply_cutoffs_enabled,
      'apply_cutoffs_enabled'
    ),
    use_new_cutoffs: resolveControl(source.use_new_cutoffs, 'use_new_cutoffs'),
  }
}

function resolveControl(
  value: unknown,
  key: keyof LockDeskCutoffControls
): boolean {
  return typeof value === 'boolean'
    ? value
    : DEFAULT_LOCK_DESK_CUTOFF_CONTROLS[key]
}

export function closeDueLockDeskOpenState(
  openState: unknown,
  due: DueLockDeskCutoff[]
): LockDeskOpenState {
  const next = normalizeLockDeskOpenState(openState)

  for (const cutoff of due) {
    next[cutoff.timezone] = {
      ...next[cutoff.timezone],
      [cutoff.product]: false,
    }
  }

  return next
}

export function normalizeCutoffTime(value: unknown): LockDeskCutoffTime {
  if (typeof value !== 'string') {
    return null
  }

  const trimmed = value.trim()
  return trimmed === '' ? null : trimmed
}

export function getScheduledLockDeskCutoffInstant(
  schedule: LockDeskCutoffSchedule | null | undefined,
  timezone: LockDeskCutoffTimezone,
  product: LockDeskCutoffProduct,
  now: Date
): Date | undefined {
  if (!schedule) {
    return undefined
  }

  const easternNow = utcToZonedTime(now, LOCK_DESK_CUTOFF_EASTERN_IANA)
  const day = LOCK_DESK_CUTOFF_DAYS[getDay(easternNow)]?.key
  if (!day) {
    return undefined
  }

  const time = schedule[day]?.[timezone]?.[product]
  if (!time || !LOCK_DESK_CUTOFF_TIME_PATTERN.test(time)) {
    return undefined
  }

  return zonedTimeToUtc(
    `${formatLocalDateStamp(easternNow)}T${time}:00`,
    LOCK_DESK_CUTOFF_EASTERN_IANA
  )
}

export function findDueLockDeskCutoffs(
  schedule: LockDeskCutoffSchedule | null | undefined,
  now: Date,
  windowMs = LOCK_DESK_CUTOFF_WINDOW_MS
): DueLockDeskCutoff[] {
  if (!schedule) {
    return []
  }

  const windowEnd = now.getTime()
  const windowStart = windowEnd - windowMs
  const due: DueLockDeskCutoff[] = []

  const easternNow = utcToZonedTime(now, LOCK_DESK_CUTOFF_EASTERN_IANA)

  for (const easternDate of [easternNow, subDays(easternNow, 1)]) {
    collectDueForEasternDay({
      schedule,
      easternDate,
      windowStart,
      windowEnd,
      due,
    })
  }

  due.sort(compareDueCutoffs)
  return due
}

const TIMEZONE_COLUMN_WIDTH = Math.max(
  ...Object.values(LOCK_DESK_CUTOFF_TIMEZONE_LABELS).map(
    (label) => label.length
  )
)
const PRODUCT_COLUMN_WIDTH = Math.max(
  ...Object.values(LOCK_DESK_CUTOFF_PRODUCT_LABELS).map((label) => label.length)
)
const EASTERN_TIME_COLUMN_WIDTH = 8

export function formatDueLockDeskCutoffLine(cutoff: DueLockDeskCutoff): string {
  const timezone = LOCK_DESK_CUTOFF_TIMEZONE_LABELS[cutoff.timezone].padEnd(
    TIMEZONE_COLUMN_WIDTH
  )
  const product =
    LOCK_DESK_CUTOFF_PRODUCT_LABELS[cutoff.product].padEnd(PRODUCT_COLUMN_WIDTH)
  const easternTime = formatInTimeZone(
    cutoff.instant,
    LOCK_DESK_CUTOFF_EASTERN_IANA,
    'h:mm a'
  ).padStart(EASTERN_TIME_COLUMN_WIDTH)

  return `${timezone} / ${product} / as of ${easternTime} ET`
}

export function formatLockDeskCutoffSlackMessage(
  cutoffs: DueLockDeskCutoff[]
): string {
  const lines = cutoffs.map(formatDueLockDeskCutoffLine).join('\n')
  return `Locks are disabled for:\n\`\`\`\n${lines}\n\`\`\``
}

function collectDueForEasternDay({
  schedule,
  easternDate,
  windowStart,
  windowEnd,
  due,
}: {
  schedule: LockDeskCutoffSchedule
  easternDate: Date
  windowStart: number
  windowEnd: number
  due: DueLockDeskCutoff[]
}): void {
  const day = LOCK_DESK_CUTOFF_DAYS[getDay(easternDate)].key
  const timezoneTimes = schedule[day]
  if (!timezoneTimes) {
    return
  }

  const easternDateStamp = formatLocalDateStamp(easternDate)

  for (const timezone of LOCK_DESK_CUTOFF_TIMEZONES) {
    const productTimes = timezoneTimes[timezone.key]
    if (!productTimes) {
      continue
    }

    for (const product of LOCK_DESK_CUTOFF_PRODUCTS) {
      const time = productTimes[product.key]
      if (!time || !LOCK_DESK_CUTOFF_TIME_PATTERN.test(time)) {
        continue
      }

      const instant = zonedTimeToUtc(
        `${easternDateStamp}T${time}:00`,
        LOCK_DESK_CUTOFF_EASTERN_IANA
      )
      const timestamp = instant.getTime()
      if (timestamp > windowStart && timestamp <= windowEnd) {
        due.push({ timezone: timezone.key, product: product.key, instant })
      }
    }
  }
}

function formatLocalDateStamp(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function compareDueCutoffs(
  left: DueLockDeskCutoff,
  right: DueLockDeskCutoff
): number {
  const byTime = left.instant.getTime() - right.instant.getTime()
  if (byTime !== 0) {
    return byTime
  }

  const byTimezone =
    LOCK_DESK_CUTOFF_TIMEZONES.findIndex(
      (timezone) => timezone.key === left.timezone
    ) -
    LOCK_DESK_CUTOFF_TIMEZONES.findIndex(
      (timezone) => timezone.key === right.timezone
    )
  if (byTimezone !== 0) {
    return byTimezone
  }

  return (
    LOCK_DESK_CUTOFF_PRODUCTS.findIndex(
      (product) => product.key === left.product
    ) -
    LOCK_DESK_CUTOFF_PRODUCTS.findIndex(
      (product) => product.key === right.product
    )
  )
}
